(() => {
  const SELECTOR = '[data-cjvr-section]';
  const cleanups = new WeakMap();

  function initSection(section) {
    if (!section || section.dataset.cjvrReady === 'true') return;
    section.dataset.cjvrReady = 'true';

    const track = section.querySelector('[data-cjvr-track]');
    const cards = Array.from(section.querySelectorAll('[data-cjvr-card]')).filter((card) => card.querySelector('video'));
    const prev = section.querySelector('[data-cjvr-prev]');
    const next = section.querySelector('[data-cjvr-next]');
    const dialog = section.querySelector('[data-cjvr-modal]');
    const videoSlot = section.querySelector('[data-cjvr-modal-video]');
    const productsSlot = section.querySelector('[data-cjvr-modal-products]');
    const modalPrev = section.querySelector('[data-cjvr-modal-prev]');
    const modalNext = section.querySelector('[data-cjvr-modal-next]');
    const closeButton = section.querySelector('[data-cjvr-close]');
    const soundButton = section.querySelector('[data-cjvr-sound]');
    const position = section.querySelector('[data-cjvr-position]');
    const finePointer = window.matchMedia('(min-width: 750px) and (hover: hover) and (pointer: fine)');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const hoverPlay = section.dataset.hoverPlay === 'true';
    const autoPlay = section.dataset.autoplayViewport === 'true';
    const previewToggle = section.querySelector('[data-cjvr-preview-toggle]');
    const visibleCards = new Set();
    const pendingCards = new Set();
    const retryCards = new Set();
    let hoveredCard = null;
    let previewsPaused = false;
    let modalOpen = false;
    let disposed = false;
    let currentIndex = 0;

    const cardVideo = (card) => card?.querySelector('video');

    function pausePreview(card) {
      const video = cardVideo(card);
      if (!video) return;
      video.pause();
      card.classList.remove('is-preview-playing');
    }

    function pauseAllPreviews(exceptCard = null) {
      cards.forEach((card) => {
        if (card !== exceptCard) pausePreview(card);
      });
    }

    function canPlayPreview(card, automatic) {
      if (disposed || document.hidden || modalOpen || reducedMotion.matches) return false;
      return automatic
        ? autoPlay && !finePointer.matches && !previewsPaused && visibleCards.has(card)
        : hoverPlay && finePointer.matches && hoveredCard === card && visibleCards.has(card);
    }

    async function playPreview(card, automatic = false) {
      if (!canPlayPreview(card, automatic)) return;
      if (pendingCards.has(card)) {
        retryCards.add(card);
        return;
      }
      const video = cardVideo(card);
      if (!video || !video.paused) return;
      if (!automatic) pauseAllPreviews(card);
      video.muted = true;
      video.playsInline = true;
      pendingCards.add(card);
      try {
        await video.play();
        if (canPlayPreview(card, automatic)) {
          card.classList.add('is-preview-playing');
        } else {
          pausePreview(card);
        }
      } catch (_) {
        card.classList.remove('is-preview-playing');
      } finally {
        pendingCards.delete(card);
        if (retryCards.delete(card) && video.paused && canPlayPreview(card, automatic)) {
          playPreview(card, automatic);
        }
      }
    }

    function syncPreviews() {
      const automatic = !finePointer.matches;
      cards.forEach((card) => {
        if (canPlayPreview(card, automatic)) {
          playPreview(card, automatic);
        } else {
          pausePreview(card);
        }
      });
    }

    previewToggle?.addEventListener('click', () => {
      previewsPaused = !previewsPaused;
      previewToggle.setAttribute('aria-pressed', String(previewsPaused));
      previewToggle.textContent = previewsPaused ? 'Пусни видеата' : 'Пауза на видеата';
      syncPreviews();
    });

    cards.forEach((card, index) => {
      const hoverZone = card.querySelector('[data-cjvr-hover-zone]');
      const openButton = card.querySelector('[data-cjvr-open]');
      const video = cardVideo(card);

      if (hoverZone) {
        hoverZone.addEventListener('mouseenter', () => {
          hoveredCard = card;
          if (finePointer.matches) syncPreviews();
        });
        hoverZone.addEventListener('mouseleave', () => {
          if (hoveredCard === card) hoveredCard = null;
          if (finePointer.matches) syncPreviews();
        });
      }

      if (video) {
        video.addEventListener('play', () => card.classList.add('is-preview-playing'));
        video.addEventListener('pause', () => card.classList.remove('is-preview-playing'));
      }

      openButton?.addEventListener('click', () => openModal(index));
    });

    function scrollStep() {
      const firstCard = track?.querySelector('[data-cjvr-card]');
      if (!track || !firstCard) return 0;
      const styles = window.getComputedStyle(track);
      const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
      return firstCard.getBoundingClientRect().width + gap;
    }

    function updateCarouselArrows() {
      if (!track || !prev || !next) return;
      const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth - 2);
      prev.disabled = track.scrollLeft <= 2;
      next.disabled = track.scrollLeft >= maxScroll;
    }

    prev?.addEventListener('click', () => {
      track?.scrollBy({ left: -scrollStep(), behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    });

    next?.addEventListener('click', () => {
      track?.scrollBy({ left: scrollStep(), behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    });

    track?.addEventListener('scroll', updateCarouselArrows, { passive: true });
    window.addEventListener('resize', updateCarouselArrows, { passive: true });
    updateCarouselArrows();

    function modalVideo() {
      return videoSlot?.querySelector('video');
    }

    function clearLoading() {
      videoSlot?.classList.remove('is-loading');
    }

    function renderModal(index) {
      if (!dialog || !videoSlot || !productsSlot || !cards.length) return;
      currentIndex = (index + cards.length) % cards.length;
      const card = cards[currentIndex];
      const sourceVideo = cardVideo(card);
      const productTemplate = card.querySelector('[data-cjvr-products-template]');

      pauseAllPreviews();
      const previousModalVideo = modalVideo();
      previousModalVideo?.pause();
      videoSlot.replaceChildren();
      productsSlot.replaceChildren();
      clearLoading();

      if (sourceVideo) {
        // Build a fresh element pointing at the sharper (<=720p) rendition instead of
        // cloning the card's lightweight 480p one.
        const cardSource = sourceVideo.querySelector('source');
        const src = card.dataset.cjvrHd || cardSource?.getAttribute('src') || '';
        const video = document.createElement('video');
        video.className = 'cjvr-modal__video';
        video.controls = true;
        video.muted = true;
        video.loop = true;
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.preload = 'metadata';
        const poster = sourceVideo.getAttribute('poster');
        if (poster) video.setAttribute('poster', poster);

        const sourceEl = document.createElement('source');
        sourceEl.setAttribute('src', src);
        sourceEl.setAttribute('type', 'video/mp4');
        video.appendChild(sourceEl);
        videoSlot.appendChild(video);

        videoSlot.classList.add('is-loading');
        video.addEventListener('loadeddata', clearLoading, { once: true });
        video.addEventListener('error', clearLoading, { once: true });
        window.setTimeout(clearLoading, 15000);

        video.load();
        video.play().catch(() => {});
      }

      if (productTemplate) productsSlot.appendChild(productTemplate.content.cloneNode(true));
      if (position) position.textContent = `${currentIndex + 1} / ${cards.length}`;
      if (soundButton) soundButton.textContent = 'Пусни звук';
    }

    function openModal(index) {
      if (!dialog || !cards.length) return;
      modalOpen = true;
      renderModal(index);
      if (typeof dialog.showModal === 'function' && !dialog.open) {
        dialog.showModal();
      } else if (!dialog.open) {
        // Fallback for browsers without <dialog> support.
        dialog.setAttribute('open', '');
        dialog.classList.add('cjvr-modal--fallback');
      }
    }

    function closeModal() {
      const video = modalVideo();
      video?.pause();
      if (dialog?.open) {
        if (typeof dialog.close === 'function') {
          dialog.close();
        } else {
          dialog.removeAttribute('open');
        }
      }
      dialog?.classList.remove('cjvr-modal--fallback');
      videoSlot?.replaceChildren();
      productsSlot?.replaceChildren();
      clearLoading();
      modalOpen = false;
      syncPreviews();
    }

    modalPrev?.addEventListener('click', () => renderModal(currentIndex - 1));
    modalNext?.addEventListener('click', () => renderModal(currentIndex + 1));
    closeButton?.addEventListener('click', closeModal);

    soundButton?.addEventListener('click', () => {
      const video = modalVideo();
      if (!video) return;
      video.muted = !video.muted;
      soundButton.textContent = video.muted ? 'Пусни звук' : 'Спри звука';
      if (video.paused) video.play().catch(() => {});
    });

    dialog?.addEventListener('click', (event) => {
      if (event.target === dialog) closeModal();
    });

    dialog?.addEventListener('close', () => {
      const video = modalVideo();
      video?.pause();
      videoSlot?.replaceChildren();
      productsSlot?.replaceChildren();
      clearLoading();
      modalOpen = false;
      syncPreviews();
    });

    let observer;
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const card = entry.target.closest('[data-cjvr-card]');
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            visibleCards.add(card);
          } else {
            visibleCards.delete(card);
          }
        });
        syncPreviews();
      }, { threshold: [0, 0.5] });
      cards.forEach((card) => observer.observe(cardVideo(card)));
    }

    function handleVisibility() {
      if (document.hidden) {
        pauseAllPreviews();
        modalVideo()?.pause();
      } else {
        syncPreviews();
      }
    }

    function handleMotion() {
      syncPreviews();
    }

    document.addEventListener('visibilitychange', handleVisibility);
    reducedMotion.addEventListener('change', handleMotion);
    finePointer.addEventListener('change', syncPreviews);
    cleanups.set(section, () => {
      disposed = true;
      observer?.disconnect();
      document.removeEventListener('visibilitychange', handleVisibility);
      reducedMotion.removeEventListener('change', handleMotion);
      finePointer.removeEventListener('change', syncPreviews);
      window.removeEventListener('resize', updateCarouselArrows);
      pauseAllPreviews();
      modalVideo()?.pause();
      if (dialog?.open) dialog.close();
      delete section.dataset.cjvrReady;
      cleanups.delete(section);
    });
  }

  function initAll(root = document) {
    if (root.matches?.(SELECTOR)) initSection(root);
    root.querySelectorAll?.(SELECTOR).forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.matches?.(SELECTOR)) cleanups.get(event.target)?.();
    event.target.querySelectorAll?.(SELECTOR).forEach((section) => cleanups.get(section)?.());
  });
})();